import { useState } from 'react';
import { CardThumbnail, SerialPhotoPanel } from './CardImage';

// Fallback foil ladder for sketch sub-sets that don't declare their own
// `sketchParallels` array (the Galaxy standard per docs/SKETCH_CARD_VISION.md).
export const DEFAULT_SKETCH_PARALLELS = ['Base', 'Silver Foil', 'Gold Foil', 'Black Foil'];

// Unlimited sketch entries for one artist row. Modeled on the serial panel in
// Checklist.js, but entries are keyed `${baseKey}__s__<i>` where i is a
// meaningless copy index — no print-run cap, no serial number to type. What
// distinguishes two sketches lives in the collections columns sketch_subject /
// sketch_parallel, surfaced here as SUBJECT and PARALLEL/FOIL editors.
export default function SketchPanel({
  baseKey, sketchKeys, cardStates, gradings, sketchMeta, photosUpdatedAtMap,
  expandedSerial, setExpandedSerial, selectState, openGradeModal,
  onAdd, onRemove, onSaveMeta, userId, refreshPhotos, sketchParallels, stateStyles,
}) {
  // Local input drafts per entry key; committed to Supabase on blur/chip click.
  const [drafts, setDrafts] = useState({});
  const presets = sketchParallels && sketchParallels.length ? sketchParallels : DEFAULT_SKETCH_PARALLELS;

  const metaFor = (sKey) => sketchMeta[sKey] || { subject: '', parallel: '' };
  const draftFor = (sKey) => drafts[sKey] !== undefined ? drafts[sKey] : metaFor(sKey).subject;

  const commitSubject = (sKey) => {
    if (drafts[sKey] === undefined) return;
    const subject = drafts[sKey].trim();
    onSaveMeta(sKey, { subject, parallel: metaFor(sKey).parallel });
    setDrafts(prev => { const n = { ...prev }; delete n[sKey]; return n; });
  };
  const setParallel = (sKey, parallel) => {
    onSaveMeta(sKey, { subject: metaFor(sKey).subject, parallel });
  };

  const sorted = [...sketchKeys].sort((a, b) => parseInt(a.split('__s__')[1], 10) - parseInt(b.split('__s__')[1], 10));

  return (
    <div style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid var(--border)', borderTop: 'none', padding: '10px' }} onClick={e => e.stopPropagation()}>
      <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '9px', color: 'var(--gold)', background: 'rgba(var(--gold-rgb),0.06)', border: '1px solid rgba(var(--gold-rgb),0.2)', borderRadius: '2px', padding: '6px 8px', marginBottom: '8px', lineHeight: 1.5 }}>
        ★ Log each sketch you own by this artist — subject, foil, photos, grade.
      </div>
      {sorted.map((sKey, idx) => {
        const sState = cardStates[sKey] || 'collected';
        const sSt = stateStyles[sState];
        const sGrading = gradings[sKey] || null;
        const meta = metaFor(sKey);
        const isEntryExpanded = expandedSerial === sKey;
        const subtitle = [meta.subject, meta.parallel].filter(Boolean).join(' · ');
        return (
          <div key={sKey} style={{ marginBottom: '6px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'6px 8px',
              border: `1px solid ${isEntryExpanded ? 'var(--cyan)' : sSt.accent+'44'}`,
              borderRadius: isEntryExpanded ? '3px 3px 0 0' : '2px',
              borderBottom: isEntryExpanded ? 'none' : undefined,
              cursor:'pointer', background: isEntryExpanded ? 'rgba(var(--cyan-rgb),0.03)' : 'transparent' }}
              onClick={e => { e.stopPropagation(); setExpandedSerial(isEntryExpanded ? null : sKey); }}>
              <div onClick={e => e.stopPropagation()}>
                <CardThumbnail userId={userId} cardKey={sKey} isOwner={true} zoom={false} stateColor={sSt.accent} photosUpdatedAt={photosUpdatedAtMap[sKey]} onImageChange={refreshPhotos} />
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'12px', color: isEntryExpanded ? 'var(--cyan)' : sSt.accent }}>SKETCH #{idx + 1}</div>
                <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'9px', color: subtitle ? 'var(--muted-3)' : 'var(--muted-2)', marginTop:'1px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{subtitle || 'No subject yet'}</div>
                <div style={{ display:'flex', gap:'4px', marginTop:'2px', flexWrap:'wrap' }}>
                  <span style={{ fontFamily:"'Orbitron',monospace", fontSize:'6px', letterSpacing:'0.05em', color:sSt.accent, border:`1px solid ${sSt.accent}44`, padding:'1px 5px', borderRadius:'2px' }}>{sSt.icon} {sState.replace('_',' ').toUpperCase()}</span>
                  {sGrading && <span style={{ fontFamily:"'Orbitron',monospace", fontSize:'6px', letterSpacing:'0.05em', color:'var(--cyan)', border:'1px solid rgba(var(--cyan-rgb),0.3)', padding:'1px 5px', borderRadius:'2px', background:'rgba(var(--cyan-rgb),0.08)' }}>{sGrading.company} {sGrading.grade}</span>}
                </div>
              </div>
              <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'11px', color:'var(--muted-2)', cursor:'pointer', padding:'0 2px' }}
                onClick={e => { e.stopPropagation(); onRemove(sKey); }}>✕</span>
            </div>
            {isEntryExpanded && (
              <div style={{ border:'1px solid var(--cyan)', borderTop:'none', borderRadius:'0 0 3px 3px', overflow:'hidden' }} onClick={e => e.stopPropagation()}>
                <div style={{ padding:'8px 10px', borderBottom:'1px solid var(--border)' }}>
                  <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.15em', color:'var(--muted-2)', marginBottom:'6px' }}>STATUS</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'4px' }}>
                    {[
                      {id:'collected', icon:'✓', label:'COLLECTED', color:stateStyles.collected.accent},
                      {id:'wishlist',  icon:'★', label:'WISHLIST',  color:stateStyles.wishlist.accent},
                      {id:'trade',     icon:'⇄', label:'TRADE',     color:stateStyles.trade.accent},
                      {id:'for_sale',  icon:'$',  label:'FOR SALE',  color:stateStyles.for_sale.accent},
                    ].map(opt => (
                      <button key={opt.id}
                        style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'6px 2px', gap:'2px',
                          border:`1px solid ${sState===opt.id ? opt.color : 'var(--border)'}`,
                          borderRadius:'2px', cursor:'pointer', background: sState===opt.id ? opt.color+'22' : 'transparent' }}
                        onClick={() => selectState(sKey, sState===opt.id ? null : opt.id)}>
                        <span style={{ fontSize:'12px', color: sState===opt.id ? opt.color : 'var(--muted-2)' }}>{opt.icon}</span>
                        <span style={{ fontFamily:"'Orbitron',monospace", fontSize:'5px', letterSpacing:'.06em', color: sState===opt.id ? opt.color : 'var(--muted-2)' }}>{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ padding:'8px 10px', borderBottom:'1px solid var(--border)' }}>
                  <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.15em', color:'var(--muted-2)', marginBottom:'6px' }}>SUBJECT / CHARACTER</div>
                  <input
                    type="text"
                    value={draftFor(sKey)}
                    onChange={e => setDrafts(prev => ({ ...prev, [sKey]: e.target.value }))}
                    onBlur={() => commitSubject(sKey)}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    placeholder="e.g. Chewbacca"
                    style={{ width:'100%', boxSizing:'border-box', background:'var(--panel)', border:'1px solid rgba(var(--cyan-rgb),0.2)', borderRadius:'2px', padding:'6px 10px', fontFamily:"'Share Tech Mono', monospace", fontSize:'12px', color:'var(--text)', outline:'none' }}
                  />
                </div>
                <div style={{ padding:'8px 10px', borderBottom:'1px solid var(--border)' }}>
                  <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.15em', color:'var(--muted-2)', marginBottom:'6px' }}>PARALLEL / FOIL</div>
                  <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>
                    {presets.map(name => (
                      <button key={name}
                        style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.06em', padding:'4px 8px', borderRadius:'2px', cursor:'pointer',
                          border:`1px solid ${meta.parallel === name ? 'var(--cyan)' : 'var(--border)'}`,
                          color: meta.parallel === name ? 'var(--cyan)' : 'var(--muted-3)',
                          background: meta.parallel === name ? 'rgba(var(--cyan-rgb),0.12)' : 'transparent' }}
                        onClick={() => setParallel(sKey, meta.parallel === name ? '' : name)}>
                        {name.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  <input
                    key={sKey + ':' + meta.parallel}
                    type="text"
                    defaultValue={presets.includes(meta.parallel) ? '' : meta.parallel}
                    onBlur={e => { const v = e.target.value.trim(); if (v) setParallel(sKey, v); else if (meta.parallel && !presets.includes(meta.parallel)) setParallel(sKey, ''); }}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    placeholder="Other foil / variant…"
                    style={{ width:'100%', boxSizing:'border-box', marginTop:'6px', background:'var(--panel)', border:'1px solid rgba(var(--cyan-rgb),0.2)', borderRadius:'2px', padding:'6px 10px', fontFamily:"'Share Tech Mono', monospace", fontSize:'11px', color:'var(--text)', outline:'none' }}
                  />
                </div>
                <div style={{ padding:'8px 10px', borderBottom:'1px solid var(--border)' }}>
                  <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.15em', color:'var(--muted-2)', marginBottom:'8px' }}>CARD PHOTOS</div>
                  <div onClick={e => e.stopPropagation()}>
                    <SerialPhotoPanel userId={userId} cardKey={sKey} stateColor={sSt.accent} photosUpdatedAt={photosUpdatedAtMap[sKey]} onImageChange={refreshPhotos} listingStatus={sState} />
                  </div>
                </div>
                <div style={{ padding:'8px 10px' }}>
                  <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.15em', color:'var(--muted-2)', marginBottom:'6px' }}>GRADE</div>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    {sGrading ? (
                      <span style={{ fontFamily:"'Orbitron',monospace", fontSize:'10px', color:'var(--cyan)', border:'1px solid rgba(var(--cyan-rgb),0.4)', background:'rgba(var(--cyan-rgb),0.08)', padding:'4px 10px', borderRadius:'2px', cursor:'pointer' }}
                        onClick={e => openGradeModal(sKey, e)}>🏆 {sGrading.company} {sGrading.grade}</span>
                    ) : (
                      <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'10px', color:'var(--muted-2)', flex:1 }}>No grade logged</span>
                    )}
                    <button style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.08em', padding:'6px 12px', borderRadius:'2px', border:'1px solid var(--muted-2)', color:'var(--muted-2)', background:'transparent', cursor:'pointer' }}
                      onClick={e => openGradeModal(sKey, e)}>
                      {sGrading ? 'EDIT' : '+ ADD GRADE'}
                    </button>
                  </div>
                </div>
                <div style={{ padding:'6px 10px', borderTop:'1px solid var(--border)', textAlign:'right' }}>
                  <button style={{ fontFamily:"'Orbitron',monospace", fontSize:'7px', letterSpacing:'.08em', padding:'4px 10px', borderRadius:'2px', border:'1px solid rgba(var(--cyan-rgb),0.3)', color:'var(--cyan)', background:'transparent', cursor:'pointer' }}
                    onClick={e => { e.stopPropagation(); setExpandedSerial(null); }}>CLOSE ✕</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
        <button
          onClick={() => onAdd()}
          style={{ flex: 1, fontFamily: "'Orbitron', monospace", fontSize: '8px', fontWeight: 700, letterSpacing: '0.08em', padding: '8px 12px', borderRadius: '2px', border: 'none', background: 'var(--green)', color: 'var(--bg)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          + ADD SKETCH
        </button>
      </div>
      <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '8px', color: 'var(--muted-2)', marginTop: '5px' }}>
        {sorted.length === 0 ? 'No sketches logged yet' : `${sorted.length} sketch${sorted.length === 1 ? '' : 'es'} logged`}
      </div>
    </div>
  );
}
